import { forwardRef, useCallback } from 'react'

import NumberFormat, { NumberFormatValues } from 'react-number-format'

import ArcadeInput, { ArcadeInputProps } from './ArcadeInput'

export interface ArcadeMoneyInputProps extends Omit<ArcadeInputProps, 'onChange' | 'defaultValue' | 'value' | 'type'> {
  /** Currency symbol to display before the value (defaults to '$') */
  currencySymbol?: string
  /** Callback when the numeric value changes (without formatting) */
  onChange: (value: string) => void
  /** Only allow input in the form of dollars and cents, dollars, or cents */
  denominationOption?: 'DOLLARS_AND_CENTS' | 'DOLLARS_ONLY' | 'CENTS_ONLY'
  value: string
}

const ArcadeMoneyInput = forwardRef<HTMLInputElement, ArcadeMoneyInputProps>(
  (
    { onChange, currencySymbol = '$', disabled, denominationOption = 'DOLLARS_AND_CENTS', name, value, ...restProps },
    ref
  ) => {
    const validateNumericWithDecimal = useCallback(
      (values: NumberFormatValues) => {
        const { floatValue } = values
        if (floatValue === undefined) return true

        if (floatValue < 0) return false
        if (denominationOption === 'CENTS_ONLY' && floatValue >= 1) return false

        return true
      },
      [denominationOption]
    )

    const decimalScale = denominationOption === 'DOLLARS_ONLY' ? 0 : 2
    const thousandSeparator = denominationOption === 'CENTS_ONLY' ? false : true

    return (
      <NumberFormat
        customInput={ArcadeInput}
        value={value}
        disabled={disabled}
        inputMode="decimal"
        thousandSeparator={thousandSeparator}
        decimalScale={decimalScale}
        // enforce two decimal places once user has shown intention to add cents
        fixedDecimalScale={value.includes('.')}
        allowNegative={false}
        allowEmptyFormatting={restProps.placeholder ? false : true}
        prefix={currencySymbol}
        isAllowed={validateNumericWithDecimal}
        onValueChange={(values: NumberFormatValues) => {
          onChange(values.value)
          if ((values.floatValue === 0 || !values.floatValue) && denominationOption !== 'CENTS_ONLY') {
            onChange('')
          }
        }}
        getInputRef={ref}
        {...restProps}
      />
    )
  }
)

ArcadeMoneyInput.displayName = 'ArcadeMoneyInput'

export default ArcadeMoneyInput
